import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { appointmentApi } from '../services/api';

export const ConfirmationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      confirmAppointment(token);
    }
  }, [token]);

  const confirmAppointment = async (confirmationToken: string) => {
    try {
      await appointmentApi.confirmAppointment(confirmationToken);
      setIsConfirmed(true);
      setError(null);
    } catch (err: any) {
      console.error('Error confirming appointment:', err);
      if (err.response?.data?.detail?.includes('ήδη επιβεβαιωθεί')) {
        setIsConfirmed(true);
        setError('Το ραντεβού σας έχει ήδη επιβεβαιωθεί.');
      } else {
        setError('Υπήρξε ένα σφάλμα κατά την επιβεβαίωση του ραντεβού σας. Παρακαλώ επικοινωνήστε μαζί μας.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-500" />
              <p className="text-gray-600">Επιβεβαίωση ραντεβού...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="bg-[#d0ddc6] text-[#2c3e50] rounded-t-lg">
          <div className="flex justify-center mb-4">
            {isConfirmed ? (
              <CheckCircle2 className="w-16 h-16 text-[#a64f3c]" />
            ) : (
              <XCircle className="w-16 h-16 text-red-500" />
            )}
          </div>
          <CardTitle className="text-center">
            {isConfirmed ? 'Επιβεβαίωση Επιτυχής!' : 'Σφάλμα Επιβεβαίωσης'}
          </CardTitle>
          <CardDescription className="text-center text-[#2c3e50]">
            {isConfirmed 
              ? error || 'Το ραντεβού σας επιβεβαιώθηκε επιτυχώς και προστέθηκε στο ημερολόγιο.'
              : error
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isConfirmed && (
              <div className="bg-[#f5f8f1] border border-[#d0ddc6] rounded-lg p-4">
                <h4 className="font-semibold text-[#2c3e50] mb-2">Χρήσιμες Οδηγίες:</h4>
                <ul className="text-sm text-[#2c3e50] space-y-1">
                  <li>• Προσέλθετε, εάν είναι δυνατό, 5 λεπτά ενωρίτερα</li>
                  <li>• Έχετε διαθέσιμο το ΑΜΚΑ και τις απαραίτητες εξετάσεις</li>
                  <li>• Σε περίπτωση ακύρωσης, παρακαλούμε ενημερώστε μας το συντομότερο</li>
                </ul>
              </div>
            )}
            <Button 
              className="w-full bg-[#d0ddc6] text-[#2c3e50] hover:bg-[#c4d1ba]" 
              onClick={() => navigate('/')}
            >
              Επιστροφή στην Αρχική
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
